import React, {
  useContext,
  ReactNode,
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
} from "firebase/auth";
import {
  auth,
  googleAuthProvider,
  //facebookAuthProvider,
} from "../config/firebase";
import { useNavigate } from "react-router-dom";
import { useLogger } from "./LoggerContext";
import { clearAllSessionDrafts } from "../helpers/SessionStorageHelpers";
import { sendLoginLink, ILoginLinkRequest } from "../api/authApi";

interface IAuthContext {
  firebaseUser: any;
  user: any;
  anonymousUser: any;
  loading: boolean;
  error: any;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: (event: React.MouseEvent) => Promise<boolean>;
  logout: (clientId?: string) => Promise<boolean | undefined>;
  register: (email: string, password: string) => Promise<any>;
  validateAuthSession: (auth: any, maxAgeMs: number) => void;
  loginWithLink: (data: ILoginLinkRequest) => Promise<any>;
}

const UserAuthContext = createContext<IAuthContext>({} as IAuthContext);

export function UserAuthContextProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { logAppInsightsError } = useLogger();
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [anonymousUser, setAnonymousUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  function login(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function register(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  // Resolves true once signed in, so callers can tell a completed sign-in from a closed or failed popup.
  async function loginWithGoogle(event: React.MouseEvent) {
    try {
      event.preventDefault();
      await signInWithPopup(auth, googleAuthProvider);
      navigate("/dashboard");
      return true;
    } catch (error) {
      logAppInsightsError(error, "Login with Google");
      return false;
    }
  }

  // async function loginWithFacebook() {
  //   try {
  //     const result = await signInWithPopup(auth, facebookAuthProvider);
  //     console.log(result);
  //     navigate("/dashboard");
  //   } catch (error) {
  //     logAppInsightsError(error);
  //   }
  // }

  const logout = useCallback(async (_clientId?: string) => {
    try {
      // Clear sessionStorage for the current user (using Firebase user.uid, which is what sessionStorage keys use)
      clearAllSessionDrafts();

      await signOut(auth);
      window.localStorage.removeItem("emailForSignIn");
      return true;
    } catch (error: any) {
      logAppInsightsError(error, "AuthContext logout Error");
      navigate("/error/500");
    }
  }, [logAppInsightsError, navigate]);

  async function loginWithLink(data : ILoginLinkRequest) {
    try {
      const response = await sendLoginLink(data);
      return response;
    } catch (error) {
      logAppInsightsError(error, "Send Login Link");
      throw error;
    }
  }

  // // Ends user session if token (last login time) is older than 1 day
  const validateAuthSession = useCallback((auth: any, maxAgeMs: number) => {
    return onAuthStateChanged(auth, async (currentUser: any) => {
      if (currentUser) {
        try {
          const tokenResult = await currentUser.getIdTokenResult();
          const authTimestampSeconds = tokenResult.claims.auth_time;

          if (authTimestampSeconds) {
            const authTimeMillis = authTimestampSeconds * 1000;

            if (Date.now() - authTimeMillis > maxAgeMs) {
              await logout();
              window.location.href = "/";
            }
          } else {
            console.warn("auth_time claim missing.");
          }
        } catch (error) {
          logAppInsightsError(error, "Validate Auth Session");
          await logout();
          window.location.href = "/";
        }
      }
    });
  }, [logAppInsightsError, logout]);

  useEffect(() => {
    const tenDaysInMs = 240 * 60 * 60 * 1000;
    const unsubscribe = validateAuthSession(auth, tenDaysInMs);

    return () => {
      unsubscribe();
    };
  }, [validateAuthSession]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (currentUser: any) => {
        setFirebaseUser(currentUser);
        setUser(currentUser && !currentUser.isAnonymous ? currentUser : null);
        setAnonymousUser(currentUser?.isAnonymous ? currentUser : null);
        setLoading(false);
        setError(null);
      },
      (authError: any) => {
        setFirebaseUser(null);
        setUser(null);
        setAnonymousUser(null);
        setLoading(false);
        setError(authError);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <UserAuthContext.Provider
      value={{
        firebaseUser,
        user,
        anonymousUser,
        loading,
        error,
        login,
        loginWithGoogle,
        //loginWithFacebook,
        logout,
        register,
        validateAuthSession,
        loginWithLink,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  return useContext(UserAuthContext);
}
